// Extracted helper functions from Emissary for easier testing

import type { Message } from '@/lib/firebase';
import type { ChatMessage as LocalChatMessage } from '@/types';
import type { FirstImpressionTool } from '@/types';

/**
 * Check if we should skip processing a tool confirmation (deduplication logic)
 */
export function shouldSkipDuplicateConfirmation(
  processedConfirmation: FirstImpressionTool[] | null,
  newConfirmation: FirstImpressionTool[] | null
): boolean {
  if (!newConfirmation) return true;
  if (!processedConfirmation) return false;
  
  return JSON.stringify(processedConfirmation) === JSON.stringify(newConfirmation);
}

/**
 * Get the last user message from a list of messages
 */
export function getLastUserMessage(messages: Message[]): Message | undefined {
  return messages
    .filter(msg => msg.sender === 'user')
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .shift();
}

/**
 * Check if we should clear the streaming message based on new Firestore messages
 */
export function shouldClearStreamingMessage(
  isStreaming: boolean,
  streamingMessage: LocalChatMessage | null,
  firestoreMessages: Message[]
): boolean {
  if (isStreaming || !streamingMessage || firestoreMessages.length === 0) {
    return false;
  }

  const latestBotMessage = firestoreMessages
    .filter(msg => msg.sender === 'assistant')
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    .shift();

  if (!latestBotMessage) return false;

  // If we got a new bot message from Firestore within 5 seconds, clear streaming message
  const timeDiff = Date.now() - (latestBotMessage.timestamp || 0);
  return timeDiff < 5000;
}

/**
 * Convert Firestore messages to local chat message format
 */
export function convertFirestoreMessagesToLocal(messages: Message[]): LocalChatMessage[] {
  return messages.map(msg => ({
    id: msg.id,
    sender: msg.sender,
    content: msg.content,
    timestamp: msg.timestamp,
    userName: msg.userName,
    userEmail: msg.userEmail,
    photoURL: msg.photoURL,
    read: msg.read,
    thinkingContent: msg.thinkingContent,
    traceId: msg.traceId,
    structuredOutput: msg.structuredOutput // Include structuredOutput field
  } as LocalChatMessage));
}

/**
 * Create a temporary bot message for streaming
 */
export function createStreamingBotMessage(
  botName: string,
  botAvatar: string,
  traceId?: string
): LocalChatMessage {
  return {
    sender: 'bot',
    content: '',
    timestamp: Date.now(),
    userName: botName,
    photoURL: botAvatar,
    traceId: traceId
  };
}

/**
 * Determine if we should trigger a bot response
 */
export function shouldTriggerBotResponse(
  activeChat: 'bot' | 'human',
  isStreaming: boolean,
  message: string,
  lastProcessedMessage: string | null
): boolean {
  if (activeChat !== 'bot') return false;
  if (isStreaming) return false;
  if (!message.trim()) return false;
  if (message === lastProcessedMessage) return false;
  
  return true;
}

/**
 * Create a message payload for Firestore
 */
export function createFirestoreMessagePayload(
  text: string,
  currentUser: { email: string; displayName?: string | null; photoURL?: string | null },
  traceId?: string
): Omit<Message, 'id' | 'timestamp'> {
  return {
    sender: 'user',
    content: text,
    userName: currentUser.displayName || 'Anonymous',
    userEmail: currentUser.email,
    photoURL: currentUser.photoURL || undefined,
    read: false,
    traceId: traceId || undefined
  };
}